import { type Dispatch, type SetStateAction, useState } from "react";
import type {
  EntityData,
  RelatieStatRomanData,
  ReprezentantRomania,
  SituatieServiciiUEData,
} from "@/types/entity";
import { validateRelatieStatRoman } from "@/utils/relatieStatValidation";
import { validateSituatieServiciiUE } from "@/utils/serviciiUeValidation";

const DEFAULT_RELATIE_STAT: RelatieStatRomanData = {
  stabilita_in_romania: null,
  sediu_principal_in_romania: null,
  entitate_administratie_publica: null,
  entitate_critica_294_2024: null,
  entitate_extra_ue: null,
  reprezentant_romania: null,
};

const DEFAULT_SITUATIE_SERVICII: SituatieServiciiUEData = {
  furnizeaza_servicii_romania: null,
  furnizeaza_servicii_ue: null,
};

type RelatieServiciiHandlers = {
  handleRelatieFieldChange: (
    field: keyof RelatieStatRomanData,
    value: boolean | null
  ) => void;
  handleReprezentantChange: (
    field: keyof ReprezentantRomania,
    value: string
  ) => void;
  handleServiciiFieldChange: (
    field: keyof SituatieServiciiUEData,
    value: boolean | null
  ) => void;
  handleStatesChange: (value: string) => void;
  handleAddressesChange: (value: string) => void;
};

type UseRelatieServiciiHandlersReturn = {
  relatieStatRoman: RelatieStatRomanData;
  situatieServiciiUE: SituatieServiciiUEData;
  entityDataForSections: Partial<EntityData>;
  sectionErrors: Record<string, string>;
  setSectionErrors: Dispatch<SetStateAction<Record<string, string>>>;
  handlers: RelatieServiciiHandlers;
  validate: () => boolean;
};

function clearError(
  setErrors: Dispatch<SetStateAction<Record<string, string>>>,
  key: string
) {
  setErrors((prev) => {
    if (!prev[key]) return prev;
    const next = { ...prev };
    delete next[key];
    return next;
  });
}

export function useRelatieServiciiHandlers(
  entityData: Partial<EntityData>
): UseRelatieServiciiHandlersReturn {
  const [relatieStatRoman, setRelatieStatRoman] =
    useState<RelatieStatRomanData>(
      entityData?.relatie_stat_roman ?? DEFAULT_RELATIE_STAT
    );

  const [situatieServiciiUE, setSituatieServiciiUE] =
    useState<SituatieServiciiUEData>(
      entityData?.situatie_servicii_ue ?? DEFAULT_SITUATIE_SERVICII
    );

  const [sectionErrors, setSectionErrors] = useState<Record<string, string>>(
    {}
  );

  const entityDataForSections = {
    ...entityData,
    relatie_stat_roman: relatieStatRoman,
    situatie_servicii_ue: situatieServiciiUE,
  };

  const handleRelatieFieldChange = (
    field: keyof RelatieStatRomanData,
    value: boolean | null
  ) => {
    setRelatieStatRoman((prev) => ({ ...prev, [field]: value }));
    clearError(setSectionErrors, `relatie_stat_roman.${field}`);
  };

  const handleReprezentantChange = (
    field: keyof ReprezentantRomania,
    value: string
  ) => {
    setRelatieStatRoman((prev) => ({
      ...prev,
      reprezentant_romania: {
        denumire: "",
        adresa: "",
        email: "",
        telefon: "",
        ...prev.reprezentant_romania,
        [field]: value,
      },
    }));
    clearError(
      setSectionErrors,
      `relatie_stat_roman.reprezentant_romania.${field}`
    );
  };

  const handleServiciiFieldChange = (
    field: keyof SituatieServiciiUEData,
    value: boolean | null
  ) => {
    setSituatieServiciiUE((prev) => {
      const updated = { ...prev, [field]: value };
      if (
        field === "furnizeaza_servicii_ue" &&
        (value === false || value === null)
      ) {
        updated.state_membre_ue = "";
        updated.adrese_ue = "";
      }
      return updated;
    });
    clearError(setSectionErrors, `situatie_servicii_ue.${field}`);
  };

  const handleStatesChange = (value: string) => {
    setSituatieServiciiUE((prev) => ({ ...prev, state_membre_ue: value }));
    clearError(setSectionErrors, "situatie_servicii_ue.state_membre_ue");
  };

  const handleAddressesChange = (value: string) => {
    setSituatieServiciiUE((prev) => ({ ...prev, adrese_ue: value }));
    clearError(setSectionErrors, "situatie_servicii_ue.adrese_ue");
  };

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {
      ...validateRelatieStatRoman(relatieStatRoman),
      ...validateSituatieServiciiUE(situatieServiciiUE),
    };
    setSectionErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  return {
    relatieStatRoman,
    situatieServiciiUE,
    entityDataForSections,
    sectionErrors,
    setSectionErrors,
    handlers: {
      handleRelatieFieldChange,
      handleReprezentantChange,
      handleServiciiFieldChange,
      handleStatesChange,
      handleAddressesChange,
    },
    validate,
  };
}
